import { Column, ColumnMeta, RowData } from '@tanstack/react-table';

import {
  NumberRangeFilter,
  OrganizationFilter,
  SelectFilter,
  TextFilter,
} from '.';
import { MultiOrganizationSelectProps } from '../../MultiOrganizationSelect';
import type { FilterTableFeatures } from '../table-features';
import { DateRangeFilter } from './DateRangeFilter';

export const ColumnFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
  TOrganization extends { id: number; name: string },
>({
  column,
  i18n,
  organizationSelect,
}: {
  column: Column<TFeatures, T, unknown>;
  i18n: {
    filterLabel: string;
    optionAll: string;
    buttonReset: string;
    buttonFilter: string;
    actionSearch: string;
    minLabel: string;
    maxLabel: string;
  };
  organizationSelect?: Omit<
    MultiOrganizationSelectProps<TOrganization>,
    'value' | 'onChange'
  >;
}) => {
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const columnMeta = column.columnDef.meta as
    | ColumnMeta<FilterTableFeatures, T>
    | undefined;
  const { filterOptions, filterType } = columnMeta || {};

  if (filterType === 'select') {
    const options =
      filterOptions === 'auto'
        ? Array.from(filterColumn.getFacetedUniqueValues().keys())
            // Filter out empty values => they represent "All" option
            .filter((s) => s !== '' && s !== null && s !== undefined)
            .toSorted()
            .map((key) => ({
              label: key,
              value: key,
            }))
        : filterOptions;

    return (
      <SelectFilter column={filterColumn} options={options || []} i18n={i18n} />
    );
  }

  if (filterType === 'date') {
    return <DateRangeFilter column={filterColumn} i18n={i18n} />;
  }

  if (filterType === 'numberRange' || filterType === 'number') {
    return <NumberRangeFilter column={filterColumn} i18n={i18n} />;
  }

  if (filterType === 'organization' && organizationSelect) {
    return (
      <OrganizationFilter
        column={filterColumn}
        i18n={i18n}
        organizationSelect={organizationSelect}
      />
    );
  }

  return <TextFilter column={filterColumn} i18n={i18n} />;
};
